import { of } from 'rxjs';
import { inject } from '@angular/core';
import { catchError, map } from 'rxjs/operators';
import { CanActivateFn, Router } from '@angular/router';

import { AuthService } from '../services/auth.service';
import { sanitizeReturnUrl } from '../../shared/utils/url.util';

/**
 * Route guard that checks user authentication state.
 * If not authenticated, redirects to sign-in with returnUrl for post-login redirect.
 */
export const authGuard: CanActivateFn = (_route, state) => {
	const router = inject(Router);
	const authService = inject(AuthService);

	const returnUrl = sanitizeReturnUrl(state.url);

	if (!authService.getAccessToken()) {
		redirectToSignIn(router, returnUrl);
		return of(false);
	}

	if (!authService.isTokenExpired()) {
		return of(true);
	}

	return authService.refreshToken().pipe(
		map(() => true),
		catchError(() => {
			authService.signOut();
			redirectToSignIn(router, returnUrl);
			return of(false);
		})
	);
};

/**
 * Redirects to sign-in page with the current URL as returnUrl parameter.
 * This allows redirecting back to the original page after successful authentication.
 *
 * @param {Router} router - The Angular router instance.
 * @param {string} returnUrl - The URL to return to after authentication.
 */
function redirectToSignIn(router: Router, returnUrl: string): void {
	void router.navigate(['/auth/sign-in'], { queryParams: { returnUrl } });
}
